import React, {
  useEffect, useState, useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';

import PaneButton from './PaneButton';
import Pane from './Pane';

import './PanesWidget.scss';

const PanesWidget = ({ panesData }) => {
  const [ activePaneId, setActivePaneId ] = useState( null );
  const [ isTransitioning, setIsTransitioning ] = useState( false );

  // Memoize initial pane ID to avoid recalculation
  const initialPaneId = useMemo( () => (
    panesData?.length > 0 ? panesData[0]?.id : null
  ), [ panesData ] );

  // Set initial active pane id onload
  useEffect( () => {
    if ( initialPaneId && !activePaneId ) {
      setActivePaneId( initialPaneId );
    }
  }, [ initialPaneId, activePaneId ] );

  // Memoize active pane data lookup to avoid repeated find operations
  const activePaneData = useMemo( () => {
    if ( !activePaneId || !panesData?.length ) return null;
    return panesData.find( ( paneData ) => paneData.id === activePaneId ) || null;
  }, [ activePaneId, panesData ] );

  // Memoize pane buttons class to avoid recalculation
  const paneButtonsClass = useMemo( () => {
    if ( !panesData || panesData.length === 0 ) return '';
    // Hide buttons if only 1 pane (just the map)
    return panesData.length <= 1 ? 'paneButtons--hidden' : '';
  }, [ panesData ] );

  // Memoize setActivePaneId callback to prevent child re-renders
  // Lock scroll BEFORE state change to prevent map from causing scroll jump
  const handleSetActivePaneId = useCallback( ( paneId ) => {
    if ( paneId !== activePaneId ) {
      const scrollY = window.scrollY;

      // Blur any focused element to prevent focus-related scroll issues
      // (e.g., Google Maps grabbing focus and causing scroll on tab switch)
      if ( document.activeElement && document.activeElement !== document.body ) {
        document.activeElement.blur();
      }

      // Lock scroll BEFORE state change
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';

      setIsTransitioning( true );
      // Small delay to allow fade-out, then change pane
      setTimeout( () => {
        setActivePaneId( paneId );
        setIsTransitioning( false );

        // Restore scroll after map has initialized
        setTimeout( () => {
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.left = '';
          document.body.style.right = '';
          document.body.style.width = '';
          window.scrollTo( 0, scrollY );
        }, 500 );
      }, 150 );
    }
  }, [ activePaneId ] );

  // Memoize pane buttons to prevent unnecessary re-renders
  const paneButtons = useMemo( () => {
    if ( !panesData || panesData.length === 0 ) return null;

    return panesData.map( ( rawPaneData ) => (
      <PaneButton
        key={rawPaneData.id}
        paneDataId={rawPaneData.id}
        btnText={rawPaneData.btnText}
        activePaneId={activePaneId}
        setActivePaneId={handleSetActivePaneId}
      />
    ) );
  }, [ panesData, activePaneId, handleSetActivePaneId ] );

  // Memoize active pane component to prevent unnecessary re-renders
  const activePaneComponent = useMemo( () => {
    if ( !activePaneData ) return null;

    return (
      <Pane
        key={activePaneId}
        paneData={activePaneData.paneData}
        activePaneId={activePaneId}
        subpaneComponent={activePaneData.subpaneComponent}
        stickyMarkup={activePaneData.stickyMarkup}
      />
    );
  }, [ activePaneData, activePaneId ] );

  // Check if active pane has skeleton data (Phase 1 only - has ID but no title/other data)
  const hasSkeletonData = activePaneData?.paneData?.length > 0 
    && (activePaneData.paneData[0].title === null || activePaneData.paneData[0].title === undefined);

  return (
    <div className="panesWidget">
      <div className={`paneButtons ${paneButtonsClass}`}>
        {paneButtons}
      </div>

      <div className={`panesWidget-frame ${hasSkeletonData ? 'panesWidget-frame--loading' : ''} ${isTransitioning ? 'panesWidget-frame--transitioning' : ''}`}>
        {activePaneComponent}
      </div>
    </div>
  );
};

PanesWidget.propTypes = {
  panesData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      btnText: PropTypes.string,
      paneData: PropTypes.any,
      subpaneComponent: PropTypes.node,
      stickyMarkup: PropTypes.node,
    })
  ),
};

PanesWidget.defaultProps = {
  panesData: null,
};

export default PanesWidget;
